import React, { useState } from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import commonObj from '../../../utils/Common';
import services from '../../../services/index';
import config from '../../../config/index';

const parseLangData = (data) => {
  let obj = data;
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data);
    } catch {
      obj = { en: data };
    }
  }
  return { en: obj?.en ?? '', mo: obj?.mo ?? '' };
};

// default fetcher: your existing getSetting(key)
const defaultFetch = (key) => services.categoryService.getSetting(key);

/**
 * Props:
 *  - label:      link text (and modal title unless `title` is given)
 *  - settingKey: key passed to the fetch function, e.g. 'TERM_AND_CONDITION'
 *  - fetchFn:    optional custom function (key) => Promise<apiResponse>
 *  - title:      optional modal title
 *  - className:  optional class for the link
 */
const SettingModalLink = ({
  label,
  settingKey,
  fetchFn = defaultFetch,
  title,
  className = "underline",
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({ en: '', mo: '' });

  const handleOpen = async (e) => {
    e.preventDefault();
    setOpen(true);
    setContent({ en: '', mo: '' });
    setLoading(true);
    try {
      const res = await fetchFn(settingKey);
      setContent(parseLangData(res?.data?.result?.data));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const close = () => setOpen(false);

  return (
    <>
      <a href="#" className={className} onClick={handleOpen}>
        {label}
      </a>

      {open &&
        createPortal(
          <div className="text-left text-black">
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
              <div className="relative my-6 mx-auto w-[90%] max-w-3xl">
                <div
                  className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"
                  style={{ maxHeight: '90vh' }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                    <h3 className="text-2xl font-semibold">{title || label}</h3>
                    <button
                      type="button"
                      className="p-1 ml-auto bg-transparent border-0 cursor-pointer text-black opacity-50 text-3xl leading-none font-semibold outline-none focus:outline-none"
                      onClick={close}
                    >
                      <span className="block h-6 w-6 text-2xl">×</span>
                    </button>
                  </div>

                  {/* Body */}
                  {loading ? (
                    <div className="p-6 text-center">{commonObj.getLabel('Please_Wait')}</div>
                  ) : (
                    <div
                      className="relative p-3 flex-auto"
                      style={{ overflowY: 'auto', minHeight: 0 }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(content?.[config.lng] || content?.en || ''),
                      }}
                    />
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
                    <button
                      type="button"
                      className="text-red-500 font-bold uppercase px-6 py-2 cursor-pointer text-sm outline-none focus:outline-none mr-1 mb-1"
                      onClick={close}
                    >
                      {commonObj.getLabel('Close')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SettingModalLink;
